import { formatCurrencyNatural, match } from '@frontend/duck-tape';
import type { MembershipWizardStep } from '@frontend/react-domain';
import { Button, Icon, Text, XStack, YStack, useIsOnMobile } from '@frontend/web-react';

export type MembershipWizardFooterProps = {
  amountDueToday: Cent;
  isDisabled: boolean;
  isLoading: boolean;
  isUpdatingPrices?: boolean;
  onContinue: () => void;
  step: MembershipWizardStep;
};

const getCtaLabel = (step: MembershipWizardStep): string =>
  match(step)
    .with('selectPlan', () => 'Continue to payment')
    .with('paymentCapture', () => 'Complete sign-up')
    .otherwise(() => 'Continue');

export const MembershipWizardFooter = ({
  amountDueToday,
  isDisabled,
  isLoading,
  isUpdatingPrices = false,
  onContinue,
  step,
}: MembershipWizardFooterProps) => {
  const isMobile = useIsOnMobile();

  const ctaLabel = getCtaLabel(step);
  const showPayTodayAmount = !isUpdatingPrices && amountDueToday > 0;

  return (
    <YStack className="border-t border-borderPrimary bg-trueWhite" flex={1} py={isMobile ? 'md' : 'lg'}>
      <YStack gapY="md">
        {showPayTodayAmount ? (
          isMobile ? (
            <YStack gapY="md">
              <XStack alignItems="center" gapX="sm" justifyContent="center">
                <Icon name="IconCoin" size={20} />
                <Text type="h6">What {"you'll"} pay today</Text>
                <Text className="italic font-serif" type="h4">
                  {formatCurrencyNatural(amountDueToday)}
                </Text>
              </XStack>
              <Button disabled={isDisabled || isLoading} isLoading={isLoading} label={ctaLabel} onClick={onContinue} />
            </YStack>
          ) : (
            <XStack alignItems="center" gapX="md" justifyContent="between">
              <XStack alignItems="center" gapX="sm">
                <XStack alignItems="center" gap="md">
                  <Text type="h5">What {"you'll"} pay today</Text>
                  <Text type="h3">{formatCurrencyNatural(amountDueToday)}</Text>
                </XStack>
              </XStack>
              {/* eslint-disable-next-line tailwindcss/no-arbitrary-value -- Fixed CTA width for consistent desktop layout */}
              <YStack className="min-w-[210px]">
                <Button
                  disabled={isDisabled || isLoading}
                  isLoading={isLoading}
                  label={ctaLabel}
                  onClick={onContinue}
                />
              </YStack>
            </XStack>
          )
        ) : isUpdatingPrices ? (
          isMobile ? (
            <YStack gapY="md">
              <XStack alignItems="center" gapX="sm" justifyContent="center">
                <Icon name="IconCoin" size={20} />
                <YStack gapY="xs">
                  <YStack bg="lineGray" height={18} radius="md" width={132} />
                  <YStack bg="lineGray" height={24} radius="md" width={80} />
                </YStack>
              </XStack>
              <Button disabled={isDisabled || isLoading} isLoading={isLoading} label={ctaLabel} onClick={onContinue} />
            </YStack>
          ) : (
            <XStack alignItems="center" gapX="md" justifyContent="between">
              <XStack alignItems="center" gapX="sm">
                <Icon name="IconCoin" size={24} />
                <YStack gapY="xs">
                  <YStack bg="lineGray" height={18} radius="md" width={144} />
                  <YStack bg="lineGray" height={24} radius="md" width={96} />
                </YStack>
              </XStack>
              {/* eslint-disable-next-line tailwindcss/no-arbitrary-value -- Fixed CTA width for consistent desktop layout */}
              <YStack className="min-w-[210px]">
                <Button
                  disabled={isDisabled || isLoading}
                  isLoading={isLoading}
                  label={ctaLabel}
                  onClick={onContinue}
                />
              </YStack>
            </XStack>
          )
        ) : (
          <Button disabled={isDisabled || isLoading} isLoading={isLoading} label={ctaLabel} onClick={onContinue} />
        )}
      </YStack>
    </YStack>
  );
};
